import React, {
  type MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgGridReact } from '@ag-grid-community/react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { Layout } from '../Layout/Layout';
import { Gutter } from '../Gutter/Gutter';
import { IconButton } from '../../element/Button/IconButton';
import { Pagination } from '../../element/Pagination/Pagination';
import { Loader } from '../../element/Loader/Loader';

import { DataGridPinnedUtil } from './data-grid-pinned-util';
import type { InternalDataGridProps, LazyLoadGridApi } from './data-grid.types';

type PaginationState = {
  data?: Array<Record<string, unknown>>;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
};
export const DataGridPagination = React.forwardRef<
  LazyLoadGridApi,
  InternalDataGridProps
>(
  (
    {
      className,
      columns,
      contained = false,
      dataSource,
      name,
      noResultsComponent,
      noRowsOverlayComponentParams,
      getContextMenuItems,
      hidePagination = false,
      onColumnMoved,
      onInternalCellClicked,
      onSelectionChanged,
      sorting,
      allowContextMenu = false,
      tooltipShowDelay,
      tooltipShowMode,
      tooltipInteraction,
    },
    ref
  ) => {
    const gridRef = useRef<LazyLoadGridApi>(null);
    // Passes ref to the parent
    useImperativeHandle(ref, () => {
      return {
        api: gridRef.current?.api,
      };
    });

    const defaultColDef = useMemo(() => {
      return {
        autoHeight: !dataSource,
        sortable: false,
      };
    }, [dataSource]);

    const [paginationState, setPaginationState] = useState<PaginationState>({
      data: [] as Array<Record<string, unknown>>,
      totalPages: 1,
      currentPage: 1,
      isLoading: true,
    });

    /*
      The same answer `DataGridInfinite` gives a source it cannot read: empty, and out of the
      loading state. Without it a `parseData` that throws - or a request that rejects - is an
      unhandled rejection behind a spinner that never clears.
    */
    const fallbackToNoRows = () => {
      setPaginationState({
        currentPage: 1,
        data: [],
        isLoading: false,
        totalPages: 0,
      });
    };

    const internalDataSource = {
      getRows: ({ page }: { page: number }) => {
        if (dataSource) {
          void dataSource
            .getData({
              sort: sorting,
              page,
            })
            .then(response => {
              try {
                const parsedResults = dataSource.parseData(response);
                const newState = {
                  totalPages: parsedResults.totalPages,
                  currentPage: parsedResults.currentPage,
                  // Use empty array instead of null for better ag-grid compatibility
                  data: (parsedResults.data || []) as Array<
                    Record<string, unknown>
                  >,
                  isLoading: false,
                };
                setPaginationState(newState);
              } catch {
                fallbackToNoRows();
              }
            })
            .catch(() => {
              fallbackToNoRows();
            });
        }
      },
    };

    /*
      The current render's `getRows`, so the grid's one-time `onGridReady` fetches with the data
      source the component holds *now* rather than the one it was built with - a screen whose ids
      arrive on a second render would otherwise fetch with the first render's connector and need a
      second request to correct itself. `DataGridInfinite` keeps `datasourceFactoryRef` for exactly
      this, and reassigns it on every render the same way.
    */
    const fetchRowsRef = useRef(internalDataSource.getRows);
    fetchRowsRef.current = internalDataSource.getRows;

    const isInitialMountRef = useRef(true);

    // Fires once per grid, and `currentPage` is 1 by definition at that point.
    const onGridReady = useCallback(() => {
      fetchRowsRef.current({ page: 1 });
    }, []);

    useEffect(() => {
      /*
        **`onGridReady` owns the first fetch.** Without this guard both it and this effect fetch on
        mount, so every consumer's first page is requested twice - `DataGridInfinite` skips its own
        mount effect for the same reason.
      */
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
        return;
      }

      fetchRowsRef.current({ page: 1 });
    }, [dataSource, sorting]);

    useEffect(() => {
      // Show/hide overlay based on data availability
      if (gridRef.current?.api) {
        if (!paginationState.data || paginationState.data.length === 0) {
          // Update overlay params and show overlay
          gridRef.current.api.setGridOption(
            'noRowsOverlayComponentParams',
            noRowsOverlayComponentParams
          );
          setTimeout(() => {
            gridRef.current?.api?.showNoRowsOverlay();
          }, 0);
        } else {
          gridRef.current.api.hideOverlay();
        }
      }
    }, [paginationState.data, noRowsOverlayComponentParams]);

    const onPageChange = (newPage: number) => {
      internalDataSource.getRows({ page: newPage });
    };

    return (
      <div
        className={AttrsHelper.formatClassname(
          className,
          contained && 'contained-grid',
          'ag-theme-quartz ag-theme-axiom theme-pagination'
        )}
        data-test={name}
        data-ready={dataSource?.hasFetched()}
      >
        {paginationState.isLoading ? (
          <Loader type="spinner" expand="cover" name="DATA_GRID_LOADING" />
        ) : null}
        <AgGridReact
          enableCellTextSelection
          ref={gridRef as MutableRefObject<AgGridReact<unknown>>}
          suppressContextMenu={!allowContextMenu}
          getContextMenuItems={getContextMenuItems}
          allowContextMenuWithControlKey
          animateRows={false}
          defaultColDef={defaultColDef}
          suppressCellFocus
          columnDefs={columns}
          maxConcurrentDatasourceRequests={1}
          noRowsOverlayComponent={noResultsComponent}
          noRowsOverlayComponentParams={noRowsOverlayComponentParams}
          rowModelType="clientSide"
          processUnpinnedColumns={DataGridPinnedUtil.unpinnable}
          onGridReady={onGridReady}
          onColumnMoved={onColumnMoved}
          suppressDragLeaveHidesColumns
          /*
            `autoHeight` here is ag-grid's *layout*, unrelated to a column's: the grid grows to the
            height of all its rows. That is the default because most consumers sit on a page that
            scrolls, and it is what `contained` turns off - the stylesheet gives `.contained-grid`
            the height instead, and the grid scrolls its own rows.
          */
          domLayout={contained ? 'normal' : 'autoHeight'}
          rowData={paginationState.data}
          rowSelection="single"
          onCellClicked={onInternalCellClicked}
          onSelectionChanged={() => {
            onSelectionChanged();
          }}
          tooltipShowDelay={tooltipShowDelay}
          tooltipShowMode={tooltipShowMode}
          tooltipInteraction={tooltipInteraction}
        />
        {/* The gutter goes with it, or a hidden control leaves its spacing behind. */}
        {hidePagination ? null : (
          <>
            <Gutter bottom="8px" />
            <Layout position="right">
              <Pagination
                /*
                  A fixed name rather than one derived from the grid's, matching the two buttons
                  below and `DATA_GRID_NO_RESULTS`. Without it the control is addressable only by
                  class, and `${name}-pagination-contents` renders as `undefined-…`.
                */
                name="DATA_GRID_PAGINATION"
                currentPage={paginationState.currentPage}
                totalPages={paginationState.totalPages}
                nextButton={
                  <IconButton
                    icon="arrow-right"
                    variation="minimal"
                    pattern="secondary"
                    onClick={() => {
                      onPageChange(paginationState.currentPage + 1);
                    }}
                    name="pagination-next-button"
                  />
                }
                prevButton={
                  <IconButton
                    icon="arrow-left"
                    variation="minimal"
                    pattern="secondary"
                    onClick={() => {
                      onPageChange(paginationState.currentPage - 1);
                    }}
                    name="pagination-prev-button"
                  />
                }
              />
            </Layout>
          </>
        )}
      </div>
    );
  }
);

DataGridPagination.displayName = 'DataGridPagination';
